import React, { useSyncExternalStore } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import {
  MoneyOff as TokenOffIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import {
  getInvitingAgent,
  subscribeInvitingAgent,
  type ShareInvitingAgent,
} from '../services/shareContext';

const useInvitingAgent = (): ShareInvitingAgent | null =>
  useSyncExternalStore(subscribeInvitingAgent, getInvitingAgent, () => null);

const agentInitials = (name: string | null | undefined, fallback: string): string => {
  const source = (name && name.trim().length > 0 ? name : fallback).trim();
  if (!source) return '?';
  return source
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

/**
 * Dialog shown when a share-link recipient tries to perform a token-spend
 * action with zero remaining tokens. Replaces the in-dialog "no tokens"
 * alert from `TokenSpendDialog` with a richer prompt pointing the user at
 * their inviting agent for a top-up.
 *
 * Reads `getInvitingAgent()` from `shareContext` — same source the
 * `PublicHeader` profile popover uses — so the recipient sees the agent's
 * photo, CEA, phone, and email.
 */
export interface OutOfTokensDialogProps {
  open: boolean;
  onClose: () => void;
}

const ContactRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | null;
  href?: string;
}> = ({ icon, label, value, href }) => {
  const display = value && value.trim().length > 0 ? value : '—';
  const isLink = Boolean(href && value && value.trim().length > 0);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.75 }}>
      <Box
        sx={{
          color: 'var(--color-accent)',
          display: 'inline-flex',
          alignItems: 'center',
          mt: '2px',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'rgba(var(--color-text-rgb), 0.6)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
        {isLink ? (
          <Typography
            component="a"
            href={href}
            sx={{
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'var(--color-accent)',
              textDecoration: 'none',
              wordBreak: 'break-all',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {display}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'var(--color-text-strong)',
              wordBreak: 'break-all',
            }}
          >
            {display}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const OutOfTokensDialog: React.FC<OutOfTokensDialogProps> = ({
  open,
  onClose,
}) => {
  const agent = useInvitingAgent();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TokenOffIcon sx={{ color: 'var(--color-accent)' }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          You have no tokens left
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
          You have used all of your available tokens for this link. To
          continue revealing prices or running searches, please contact{' '}
          {agent?.name ? <strong>{agent.name}</strong> : 'your Huttons agent'}
          {' '}for more information.
        </Typography>

        {agent && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1.5,
                p: 1.25,
                borderRadius: 1,
                bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.08)',
                border: '1px solid rgba(var(--color-accent-rgb, 232, 160, 70), 0.25)',
              }}
            >
              <Avatar
                src={agent.photo ?? undefined}
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.2)',
                  color: 'var(--color-text-strong)',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '& img': { objectFit: 'contain', objectPosition: 'center top' },
                }}
              >
                {agentInitials(agent.name, agent.ceaNumber)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  {agent.name || 'Your Huttons Agent'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}
                >
                  Huttons Asia Pte Ltd
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 1 }} />

            <ContactRow
              icon={<BadgeIcon fontSize="small" />}
              label="CEA Number"
              value={agent.ceaNumber}
            />
            <ContactRow
              icon={<PhoneIcon fontSize="small" />}
              label="Phone"
              value={agent.mobile}
              href={agent.mobile ? `tel:${agent.mobile}` : undefined}
            />
            <ContactRow
              icon={<EmailIcon fontSize="small" />}
              label="Email"
              value={agent.email}
              href={agent.email ? `mailto:${agent.email}` : undefined}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OutOfTokensDialog;
